import {
  Blockquote,
  Em,
  H1,
  H2,
  H3,
  Li,
  P,
  ProseAnchor,
  Strong,
  Ul,
} from '../Prose';
export default function SectionMaintenanceTickets() {
  return (
    <>
      <H1>Maintenance Tickets</H1>

      <P>
        We manage the maintenance of software we create for clients in this
        management system, under the <Strong>Tickets</Strong> tab of each
        Company.
      </P>

      <P>
        If you are working on resolving a ticket for a client, you are
        encouraged to keep them updated via the ticket directly yourself. But
        there are important guidelines to follow to make sure we are all using
        the ticket system consistently, which are described in this section.
      </P>

      <H2>Ticket statuses</H2>

      <P>
        Tickets can be in the following key statuses, controlled by tags being
        added/removed from the ticket:
      </P>

      <Ul>
        <Li>
          <Strong>Resolved</Strong> (blue background) - we have taken
          appropriate action, and we believe the ticket is now resolved.
          Importantly, <Em>we don't "close" the ticket ourselves</Em> - we let
          the client do that if they are happy with our resolution.
        </Li>
        <Li>
          <Strong>Other resolution reached</Strong> (grey background) - we don't
          believe any further work is needed on the ticket, but not because of
          us solving the problem. It will be a technicality like it's a
          duplicate of another issue, or what they thought is a bug is actually
          expected behaviour.
        </Li>
        <Li>
          <Strong>Info needed</Strong> (red background) - we can't take any
          further action until the client replies with further information we
          have requested.
        </Li>
        <Li>
          <Strong>Other blocker</Strong> (no background) - we can't take any
          further action, likely because of some third party blocker, but not
          the client's fault.
        </Li>
        <Li>
          <Strong>Active</Strong> (no background) - if the ticket has no blocker
          or resolution tags, then it means it's an active issue which we should
          be working on, up to the client's allotted maintenance quota.
        </Li>
      </Ul>

      <H2>Time logging</H2>

      <P>
        Any time spent working on maintenance tickets should be logged on the
        ticket, so that we know when we've reached the number of hours in the
        client's maintenance quota.
      </P>

      <P>
        This can be done by clicking the stopwatch icon and entering the number
        of minutes worked. Do this as soon as the work is done so you don't
        forget.
      </P>

      <H2>Updating tickets</H2>

      <P>
        Any <Strong>comment</Strong> you make on a ticket will generally notify
        the client (although it may depend on their notification preferences),
        but adding tags will not.
      </P>

      <P>
        If you are going to comment, notifying the client, make sure it's either
        something they can action, or important information you're conveying to
        them. I.e. don't waste their time with inter-team discussion (we can do
        that in person or on Slack).
      </P>

      <P>
        There is no way yet to specifically tag another user, but feel free to
        start your reply with "Hi [first name]," if replying to a previous
        comment by the client, or "@[first name]" if trying to catch their
        attention on a ticket they haven't been involved in yet.
      </P>

      <P>
        The general reasons you would update a ticket are listed below, along
        with example replies (feel free to copy/paste them as templates).
      </P>

      <H3>Resolved</H3>

      <P>
        If you have resolved the problem in a ticket (e.g. fixed a bug, or
        answered a question), then you can give it the <Strong>resolved</Strong>{' '}
        tag, and leave a comment letting the client know.
      </P>

      <P>
        It must be resolved <Em>in a way the client can verify</Em>. E.g. if you
        have just made a PR on GitHub, don't tell the client about that yet!
        Wait until it is at least on the M10c Releases tab in a way they can try
        out for themselves (whoever is acting as the{' '}
        <Strong>Release Manager</Strong> will let you know when this is). You
        should then mention this version in your reply, so they can easily
        understand how they can verify the resolution themselves:
      </P>

      <Blockquote>Fixed in v1.0.0</Blockquote>

      <P>
        The above very brief reply is fine for small bugs, but if it was
        something serious, or a large task, it's nice to provide them some extra
        context. E.g. explain how the problem originated and why the steps we've
        taken mean a similar problem shouldn't happen again.
      </P>

      <Blockquote>
        Fixed in v1.0.0. This was caused by a lack of error checking in the old
        codebase, but we've corrected the issue and added full tests so this
        should never happen again.
      </Blockquote>

      <P>
        If you are not fully confident that your resolution completely solves
        the client's issue, feel free to tag it anyway, but remind them they are
        welcome to remove the tag if they disagree. E.g. when answering a
        question:
      </P>

      <Blockquote>
        <P>Yes, that's right, it's not possible for a user to do that.</P>

        <P>
          I'm marking this as resolved as I believe that answers your question,
          but feel free to let us know and remove the tag if you need further
          support on this.
        </P>
      </Blockquote>

      <ProseAnchor id="fixed-before-next-release" />
      <H3>Fixed before next release</H3>

      <P>
        If you have fixed the issue at a point where it will be more than a
        couple of days until the next release will be tested and ready to launch
        (e.g. because previous release is still rolling out), it's worth keeping
        the client in the loop:
      </P>

      <Blockquote>
        <P>
          We have identified the root cause of this bug and are preparing a fix
          for the next release.
        </P>
      </Blockquote>

      <P>
        You can then tag the ticket with the blocker "Next Release", to indicate
        that there's no further work we'll be doing on it until the next release
        is ready.
      </P>

      <H3>Info Needed</H3>

      <P>
        The Info Needed indicates that we are halting work on the ticket until
        required information is provided, so only use this if lack of
        information from the client is a complete blocker in making any further
        progress on the ticket. (If not, you can still ask questions without
        adding the tag).
      </P>

      <P>
        If you are going to block the ticket with this tag, try to think ahead
        and make sure you are asking for everything you will likely need to
        reach a resolution (i.e. you don't want to be in back-and-forth replies
        with the client continually marking it as Info Needed).
      </P>

      <P>
        Try to keep the questions the client needs to answer direct and clear,
        e.g. if there are multiple things needed write a numbered list.
      </P>

      <H3>Duplicate</H3>

      <P>
        We want to keep one ticket per "defect", not have information spread
        over lots of different places. So if a client opens a ticket that's for
        the same issue as something described previously, you can tag it with
        the <Strong>duplicate</Strong> resolution, and reply referencing the
        older ticket:
      </P>

      <Blockquote>
        This looks like a duplicate of #100. Please could we keep any further
        relevant discussion on that ticket.
      </Blockquote>

      <P>
        If there is any important new information in this ticket, you can copy
        and paste it into a comment on the older ticket. (Use &gt; to quote it
        so it doesn't look like you're the original one saying it).
      </P>
    </>
  );
}
